import React from 'react';
import styled, { css } from 'styled-components';
import { Body, H1 } from '@components/dashboard/Typography';
import Video from '@components/VideoModule';
import { PageSlice, SliceVideoHalfRowDetails, VideoHalfRowDetailsInfoType } from '@contracts';
import useNavigate from '@utils/useNavigate';
import Button from './Button';
import useDesktop from './useDesktop';
import VideoHalfRowPadsInfo from './VideoHalfRowPadsInfo';

interface Props {
  slice: PageSlice;
}

const VIEWPORT_BREAKPOINT = 1024;

const VideoHalfRow: React.FC<Props> = ({ slice }) => {
  const { navigateWrapper } = useNavigate();
  const desktop = useDesktop(VIEWPORT_BREAKPOINT);
  const slicePrimary = slice?.primary as SliceVideoHalfRowDetails;
  const background = slicePrimary?.background_css?.text;
  const mobileBackground = slicePrimary?.mobile_background_css?.text;
  const textColor = desktop ? slicePrimary?.text_color : slicePrimary?.mobile_text_color;
  const headingText = slicePrimary?.heading?.text;
  const subtitleText = slicePrimary?.subtitle?.text;
  const desktopVideo = slicePrimary?.desktop_video;
  const mobileVideo = slicePrimary?.mobile_video;
  const desktopImage = slicePrimary?.desktop_placeholder_image;
  const mobileImage = slicePrimary?.mobile_placeholder_image;
  const buttonText = slicePrimary?.button_text?.text;
  const buttonLink = slicePrimary?.button_link?.text;
  const image = desktop ? desktopImage : mobileImage;
  const video = desktop ? desktopVideo : mobileVideo;
  const textInfoType = slicePrimary?.text_info_type;
  const mobileInfoCoversVideo = slicePrimary?.mobile_info_covers_video;

  const renderInfoColumn = () => {
    switch (textInfoType) {
      case VideoHalfRowDetailsInfoType.PadsInfo:
        return <VideoHalfRowPadsInfo />;
      default:
        return (
          <TextContent>
            <TextWrap>
              <StyledH1 color={textColor} className="video-half-row-heading">
                {headingText}
              </StyledH1>
              <StyledBody color={textColor}>{subtitleText}</StyledBody>
            </TextWrap>
            <Button onClick={handleClick}>{buttonText}</Button>
          </TextContent>
        );
    }
  };

  const handleClick = () => {
    if (buttonLink) {
      navigateWrapper(buttonLink);
    }
  };

  return (
    <Container $background={background} $mobileBackground={mobileBackground}>
      <InnerContainer $mobileInfoCoversVideo={mobileInfoCoversVideo}>
        <TextColumn $mobileInfoCoversVideo={mobileInfoCoversVideo}>{renderInfoColumn()}</TextColumn>
        <VideoColumn>
          <Video mp4={video?.url} placeholder={image?.url || undefined} cover />
        </VideoColumn>
      </InnerContainer>
    </Container>
  );
};

export default VideoHalfRow;

const Container = styled.div<{
  $background?: string;
  $mobileBackground?: string;
}>`
  background: ${(p) => p.$background ?? 'linear-gradient(180deg, #E1EED3 0%, #DCE9F2 100%)'};
  width: 100%;

  @media (max-width: ${VIEWPORT_BREAKPOINT}px) {
    background: ${(p) => p.$mobileBackground ?? 'linear-gradient(0deg, rgba(56, 78, 46, 0.5) 0%, rgba(0, 0, 0, 0) 100%)'} !important;
  }
`;

const InnerContainer = styled.div<{
  $mobileInfoCoversVideo?: boolean;
}>`
  display: flex;
  flex-direction: row;
  width: 100%;
  position: relative;
  max-width: 1440px;
  margin: 0 auto;

  ${(p) =>
    p.$mobileInfoCoversVideo
      ? css`
          height: 100vh;
          max-height: 74vh;
        `
      : css`
          height: auto;
        `}

  @media (max-width: ${VIEWPORT_BREAKPOINT}px) {
    ${(p) =>
      p.$mobileInfoCoversVideo
        ? css`
            display: block;
            max-height: 83vh;
          `
        : css`
            display: flex;
            height: auto;
            flex-direction: column-reverse;
          `}
  }
`;

const Column = styled.div<{ $mobileInfoCoversVideo?: boolean }>`
  width: 50%;
  position: relative;
  z-index: 1;
  overflow: hidden;

  @media (max-width: ${VIEWPORT_BREAKPOINT}px) {
    width: 100%;
    height: 100%;
  }
`;

const TextColumn = styled(Column)<{
  $mobileInfoCoversVideo?: boolean;
}>`
  display: flex;
  align-items: center;
  justify-content: center;

  @media (max-width: ${VIEWPORT_BREAKPOINT}px) {
    ${(p) =>
      p.$mobileInfoCoversVideo
        ? css`
            position: absolute;
            z-index: 2;
            bottom: 0;
            align-items: flex-end;
          `
        : css`
            align-items: start;
            height: auto;
          `}
  }
`;

const TextContent = styled.div`
  display: inline-flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  gap: 32px;
  width: 100%;
  max-width: 500px;

  @media (max-width: ${VIEWPORT_BREAKPOINT}px) {
    padding: 0 20px 40px;
    align-items: center;
  }

  & > button {
    width: 208px;
    padding: 0 25px;
    justify-content: center;
    align-items: center;
    border-radius: 5px;
  }
`;

const TextWrap = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  gap: 16px;
`;

const StyledH1 = styled(H1)`
  text-shadow: 0px 4px 30px rgba(0, 0, 0, 0.05);
  font-size: 62px;
  font-style: normal;
  font-weight: 600;
  line-height: 110%; /* 68.2px */
  letter-spacing: -0.62px;
  transition: color 0.2s ease-in-out;

  @media (max-width: ${VIEWPORT_BREAKPOINT}px) {
    text-align: center;
    font-size: 36px !important;
    letter-spacing: -0.36px;
  }
`;

const StyledBody = styled(Body)`
  font-size: 18px;
  line-height: 150%;
  transition: color 0.2s ease-in-out;

  @media (max-width: ${VIEWPORT_BREAKPOINT}px) {
    display: none;
  }
`;

const VideoColumn = styled(Column)<{ $mobileInfoCoversVideo?: boolean }>`
  ${(p) =>
    p.$mobileInfoCoversVideo
      ? ''
      : css`
          height: 720px;
        `}

  @media (max-width: ${VIEWPORT_BREAKPOINT}px) {
    ${(p) =>
      p.$mobileInfoCoversVideo
        ? ''
        : css`
            height: 600px;
          `}
  }

  & > .video-container {
    height: 100%;
    max-height: 100%;
  }
`;
